import { Info } from "lucide-react";
import type { JSX } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip.js";

/**
 * Small "?" info affordance: an info icon that reveals an explanatory tooltip
 * on hover/focus. Used beside metric labels so a user can learn what a number
 * means without leaving the page.
 *
 * The trigger is a focusable <span> (not a <button>) so it stays valid HTML
 * when a metric card is itself wrapped in a link, and clicking it never
 * triggers the card's navigation.
 */
export function InfoHint({
  text,
  label = "What is this metric?",
}: {
  text: string;
  label?: string;
}): JSX.Element {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            aria-label={label}
            onClick={(e) => e.preventDefault()}
            className="inline-flex cursor-help items-center rounded text-muted-foreground/50 transition-colors hover:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
